import axiosInstance, { getAccessToken } from './axiosConfig';
import { requireAuthToken } from '../utils/authUtils';

const buildApiError = (error, fallbackMessage) => {
    const backend = error?.response?.data;
    const message = backend?.message || error?.message || fallbackMessage;
    const normalized = new Error(message);
    normalized.backend = backend;
    if (backend?.duplicateId) normalized.duplicateId = backend.duplicateId;
    return normalized;
};

const vendorAPI = {
    // Get all vendors with pagination
    getVendors: async (params = {}) => {
        try {
            requireAuthToken(getAccessToken);
            console.log('Fetching vendors with params:', params);
            const response = await axiosInstance.get('/vendors', { params });
            
            // DEBUG: Log the full response structure
            /*
            console.log('Vendor API Response:', {
                fullResponse: response,
                data: response.data,
                success: response.data?.success,
                vendorsData: response.data?.data?.vendors,
                statsData: response.data?.data?.stats,
                paginationData: response.data?.data?.pagination
            });
            */
            return response.data;
        } catch (error) {
            console.error('Vendor API Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to fetch vendors');
        }
    },
    getAllVendors: async () => {
        try {
            requireAuthToken(getAccessToken);
            const response = await axiosInstance.get('/vendors');
            
            // Try to extract vendors from different response structures
            let vendors = [];
            
            if (Array.isArray(response.data)) {
                vendors = response.data;
            } else if (response.data?.data?.vendors) {
                vendors = response.data.data.vendors;
            } else if (response.data?.vendors) {
                vendors = response.data.vendors;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                vendors = response.data.data;
            } else if (response.data?.success && response.data?.data) {
                vendors = Array.isArray(response.data.data) 
                    ? response.data.data 
                    : response.data.data.vendors || [];
            } else {
                console.error('Unknown response structure:', response.data);
                vendors = [];
            }
            
            return {
                success: true,
                data: vendors,
                stats: response.data?.stats || response.data?.data?.stats || null
            };
        } catch (error) {
            console.error('Error in getAllVendors:', error);
            throw error;
        }
    },

    // Search vendors for autocomplete
    searchVendors: async (query, limit = 10) => {
        try {
            requireAuthToken(getAccessToken);
            // Make sure query is at least 2 characters as per backend requirement
            if (!query || query.trim().length < 2) {
                return [];
            }
            
            const response = await axiosInstance.get('/vendors/search', {
                params: { q: query, limit }
            });
            
            /*
            console.log('Search API Response:', {
                url: '/vendors/search',
                query: query,
                response: response.data
            });
            */
            // Your API returns: { success: true, data: [...] }
            // Return the array of formatted vendors
            return response.data.data || [];
        } catch (error) {
            console.error('Vendor Search Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to search vendors');
        }
    },

    // Get single vendor
    getVendor: async (vendorId) => {
        try {
            requireAuthToken(getAccessToken);
            const response = await axiosInstance.get(`/vendors/${vendorId}`);
            return response.data;
        } catch (error) {
            console.error('Vendor Get Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to fetch vendor');
        }
    },

    // Create new vendor
    createVendor: async (vendorData) => {
        try {
            requireAuthToken(getAccessToken);
            const response = await axiosInstance.post('/vendors', vendorData);
            return response.data;
        } catch (error) {
            console.error('Vendor Create Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to create vendor');
        }
    },

    // Update vendor
    updateVendor: async (vendorId, vendorData) => {
        try {
            requireAuthToken(getAccessToken);
            const response = await axiosInstance.put(`/vendors/${vendorId}`, vendorData);
            return response.data;
        } catch (error) {
            console.error('Vendor Update Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to update vendor');
        }
    },

    // Delete vendor
    deleteVendor: async (vendorId) => {
        try {
            requireAuthToken(getAccessToken);
            const response = await axiosInstance.delete(`/vendors/${vendorId}`);
            return response.data;
        } catch (error) {
            console.error('Vendor Delete Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to delete vendor');
        }
    },

    // Get vendor transactions
    getVendorTransactions: async (vendorId, params = {}) => {
        try {
            requireAuthToken(getAccessToken);
            const response = await axiosInstance.get(`/vendors/${vendorId}/transactions`, { params });
            return response.data;
        } catch (error) {
            console.error('Vendor Transactions Error:', error.response?.data || error.message);
            throw buildApiError(error, 'Failed to fetch vendor transactions');
        }
    },

    // Helper function to format TIN
    formatTIN: (tin) => {
        if (!tin) return '';
        const digits = tin.replace(/\D/g, '');
        if (digits.length === 12) {
            return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}-${digits.slice(9, 12)}`;
        }
        return tin;
    },

    // Validate TIN format
    validateTIN: (tin) => {
        if (!tin) return true; // TIN is optional
        const tinRegex = /^\d{3}-\d{3}-\d{3}-\d{3}$|^\d{12}$/;
        return tinRegex.test(tin);
    },

    // Get vendor types for dropdown
    getVendorTypes: () => {
        return [
            { value: 'supplier', label: 'Supplier' },
            { value: 'contractor', label: 'Contractor' },
            { value: 'professional', label: 'Professional' },
            { value: 'landlord', label: 'Landlord' },
            { value: 'other', label: 'Other' }
        ];
    },

    // Get business types for dropdown
    getBusinessTypes: () => {
        return [
            { value: 'individual', label: 'Individual' },
            { value: 'corporation', label: 'Corporation' },
            { value: 'partnership', label: 'Partnership' },
            { value: 'government', label: 'Government' }
        ];
    },

    // Get status options
    getStatusOptions: () => {
        return [
            { value: 'active', label: 'Active' },
            { value: 'inactive', label: 'Inactive' },
            { value: 'suspended', label: 'Suspended' }
        ];
    }
};

export default vendorAPI;